import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { readAllRowsKeyset } from '@/lib/supabase-pagination';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ThreadStatus =
  | 'all'
  | 'needs_reply'
  /**
   * Not a value of `os_conversation_threads.status`. A deferred conversation stays `resolved`; this
   * tab is defined by a DATE — `follow_up_due_at <= now()` — which is why it is excluded from
   * `StoredThreadStatus` below, exactly like `reply_queue`.
   */
  | 'follow_up'
  | 'waiting'
  | 'resolved'
  | 'ghosted'
  | 'active'
  | 'sent'
  | 'deleted'
  | 'reply_queue';

export interface ConversationThread {
  thread_id: string;
  organization_id: string;
  user_id: string | null;
  contact_id: string | null;
  heyreach_lead_id: string;
  heyreach_campaign_id: string;
  campaign_name: string | null;
  lead_name: string;
  lead_title: string | null;
  lead_company: string | null;
  lead_linkedin_url: string | null;
  status: string;
  current_stage: string;
  outcome: string | null;
  message_count: number;
  our_message_count: number;
  their_message_count: number;
  pending_draft_count: number;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender: string | null;
  initial_outreach_message: string | null;
  sender_id: string | null;
  sender_name: string | null;
  created_at: string;
  updated_at: string;
  /** When a gate-deferred conversation becomes touchable again. NULL for everything else. */
  follow_up_due_at: string | null;
}

export interface ThreadFilters {
  status: ThreadStatus;
  senderId: string | 'all' | 'unassigned';
  campaignId: string | 'all';
  search: string;
  page: number;
  pageSize: number;
}

export interface ThreadQueryResult {
  threads: ConversationThread[];
  total: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
  campaignId: string | 'all';
}

export interface ConversationCampaign {
  organization_id: string | null;
  heyreach_campaign_id: string | null;
  campaign_name: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How often the clock-dependent queries re-ask. 60s against a 45-day timer is far more precision
 * than the deadline deserves; it is chosen to be short enough that "it never appeared" is never the
 * operator's experience, and long enough to stay invisible in cost.
 */
const FOLLOW_UP_REFETCH_MS = 60_000;

const DEFAULT_FILTERS: ThreadFilters = {
  status: 'needs_reply',
  senderId: 'all',
  campaignId: 'all',
  search: '',
  page: 1,
  pageSize: 20,
};

export const THREAD_COLUMNS = `
  thread_id,
  organization_id,
  user_id,
  contact_id,
  heyreach_lead_id,
  heyreach_campaign_id,
  campaign_name,
  lead_name,
  lead_title,
  lead_company,
  lead_linkedin_url,
  status,
  current_stage,
  outcome,
  message_count,
  our_message_count,
  their_message_count,
  pending_draft_count,
  last_message_at,
  last_message_preview,
  last_message_sender,
  initial_outreach_message,
  sender_id,
  sender_name,
  follow_up_due_at,
  created_at,
  updated_at
` as const;

// ─────────────────────────────────────────────────────────────────────────────
// Filter Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useThreadFilters = (): {
  filters: ThreadFilters;
  /**
   * DIFF r3 F2: `replaceHistory` is part of the CONTRACT and must be on the public type. The
   * implementation has honoured it since 7dcefa9, but the narrow annotation here erased it — under
   * TS strict an object literal carrying it is an excess property (TS2353), so no caller could
   * request the behaviour the doc block below tells them to request. A normalizer that cannot pass
   * it pushes a history entry, and Back then restores the invalid parameter, which the normalizer
   * clears again: the operator ping-pongs between two states instead of leaving the page.
   */
  setFilters: (updates: Partial<ThreadFilters> & { replaceHistory?: boolean }) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo((): ThreadFilters => {
    const statusParam = searchParams.get('status') as ThreadStatus | null;
    const senderParam = searchParams.get('sender');
    const campaignParam = searchParams.get('campaign');
    const searchParam = searchParams.get('q') || DEFAULT_FILTERS.search;
    const pageParam = Number.parseInt(searchParams.get('page') || '1', 10);
    const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
    const allowedStatuses: ThreadStatus[] = [
      'needs_reply',
      'follow_up',
      'waiting',
      'resolved',
      'ghosted',
      'active',
      'sent',
      'deleted',
      'all',
      'reply_queue',
    ];
    const status = statusParam && allowedStatuses.includes(statusParam)
      ? statusParam
      : DEFAULT_FILTERS.status;

    return {
      status,
      senderId: senderParam || DEFAULT_FILTERS.senderId,
      campaignId: campaignParam || DEFAULT_FILTERS.campaignId,
      search: searchParam,
      page,
      pageSize: DEFAULT_FILTERS.pageSize,
    };
  }, [searchParams]);

  /**
   * DIFF round 4, F2: every filter change used to `replace`, so choosing Campaign B from the
   * dropdown left no Campaign A entry in history and Back skipped to whatever preceded the inbox
   * entirely. The plan claimed this filter was "shareable, back-button-correct"; `replace` made
   * the second half false.
   *
   * A deliberate operator choice — campaign, status, sender — is a navigation and gets a history
   * entry. Typing in the search box is not: it fires per keystroke and would bury the back stack,
   * so it keeps `replace`, as does plain pagination. Callers that are normalising an invalid
   * parameter rather than expressing an intent pass `replaceHistory: true` explicitly.
   */
  const setFilters = useCallback(
    (updates: Partial<ThreadFilters> & { replaceHistory?: boolean }) => {
      const newParams = new URLSearchParams(searchParams);
      if (updates.status !== undefined) {
        newParams.set('status', updates.status);
      }
      if (updates.senderId !== undefined) {
        newParams.set('sender', updates.senderId);
      }
      if (updates.campaignId !== undefined) {
        newParams.set('campaign', updates.campaignId);
      }
      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set('q', updates.search);
        } else {
          newParams.delete('q');
        }
      }
      if (updates.page !== undefined) {
        newParams.set('page', String(updates.page));
      }
      if (
        updates.status !== undefined
        || updates.senderId !== undefined
        || updates.campaignId !== undefined
        || updates.search !== undefined
      ) {
        newParams.set('page', '1');
      }
      const isDeliberateChoice =
        updates.status !== undefined ||
        updates.senderId !== undefined ||
        updates.campaignId !== undefined;
      const replace = updates.replaceHistory ?? !isDeliberateChoice;
      setSearchParams(newParams, { replace });
    },
    [searchParams, setSearchParams]
  );

  return { filters, setFilters };
};

// ─────────────────────────────────────────────────────────────────────────────
// Data Hook
// ─────────────────────────────────────────────────────────────────────────────

export interface UseConversationThreadsOptions {
  /**
   * Set to false to skip this query entirely (no network call). Used by the
   * Reply Queue tab, whose rows come from `useReplyQueue` — the drafts-truth
   * source — instead of this status-column-backed view.
   */
  enabled?: boolean;
}

const isPageOnlyFilterChange = (
  currentFilters: ThreadFilters,
  previousFilters: unknown,
): boolean => {
  if (!previousFilters || typeof previousFilters !== 'object') return false;

  const previous = previousFilters as Partial<ThreadFilters>;
  return previous.page !== currentFilters.page
    && previous.pageSize === currentFilters.pageSize
    && previous.status === currentFilters.status
    && previous.senderId === currentFilters.senderId
    && previous.campaignId === currentFilters.campaignId
    && previous.search === currentFilters.search;
};

export const useConversationThreads = (
  filters: ThreadFilters,
  options: UseConversationThreadsOptions = {}
): ReturnType<typeof useQuery<ThreadQueryResult>> => {
  const queryClient = useQueryClient();
  const { user, isAdmin, organizationId } = useAuth();
  const userId = user?.id ?? null;
  const { enabled = true } = options;

  const query = useQuery<ThreadQueryResult>({
    queryKey: ['conversation-threads', userId, organizationId, filters],
    enabled: enabled && userId !== null && organizationId !== null,
    queryFn: async () => {
      const { page, pageSize, status, senderId, campaignId } = filters;
      const offset = (page - 1) * pageSize;
      const search = filters.search.trim();

      if (userId === null || organizationId === null) {
        return {
          threads: [],
          total: 0,
          hasMore: false,
          page,
          pageSize,
          campaignId,
        };
      }

      const source = status === 'deleted'
        ? 'os_conversation_trash'
        : 'os_conversation_inbox';
      // Follow-up is ordered by the DEADLINE, ascending — the most overdue person first. Every
      // other tab is a stream, newest on top; this one is a queue with a debt.
      const isFollowUp = status === 'follow_up';
      let queryBuilder = supabase
        .from(source)
        .select(THREAD_COLUMNS, { count: 'exact' })
        .eq('organization_id', organizationId)
        .order(isFollowUp ? 'follow_up_due_at' : 'last_message_at', { ascending: isFollowUp })
        .range(offset, offset + pageSize - 1);

      if (status === 'sent') {
        queryBuilder = queryBuilder.gt('our_message_count', 0);
      } else if (isFollowUp) {
        // DUE only, never "scheduled at some point". The badge counts this same set, so the number
        // on the tab and the rows behind it can never tell two different stories.
        queryBuilder = queryBuilder.lte('follow_up_due_at', new Date().toISOString());
      } else if (status !== 'all' && status !== 'deleted') {
        queryBuilder = queryBuilder.eq('status', status);
      }

      if (senderId === 'unassigned') {
        queryBuilder = queryBuilder.is('sender_id', null);
      } else if (senderId !== 'all') {
        queryBuilder = queryBuilder.eq('sender_id', senderId);
      }

      if (campaignId !== 'all') {
        queryBuilder = queryBuilder.eq('heyreach_campaign_id', campaignId);
      }

      if (search.length > 0) {
        const safeSearch = search.replace(/,/g, ' ');
        queryBuilder = queryBuilder.or(
          `lead_name.ilike.%${safeSearch}%,lead_company.ilike.%${safeSearch}%,last_message_preview.ilike.%${safeSearch}%`
        );
      }

      const { data, error, count } = await queryBuilder;
      if (error) throw error;

      return {
        threads: (data || []) as ConversationThread[],
        total: count || 0,
        hasMore: (count || 0) > offset + pageSize,
        page,
        pageSize,
        campaignId,
      };
    },
    staleTime: 10000,
    // Due-ness is a function of the clock, and React Query has no clock: `staleTime` marks data
    // stale, it does not refetch a mounted query (see the note further down, and F5 of the S20
    // review). Without this, an operator sitting on an empty Follow-up tab one minute before a
    // deadline would keep seeing an empty tab afterwards. Only this tab pays the cost.
    refetchInterval: filters.status === 'follow_up' ? FOLLOW_UP_REFETCH_MS : false,
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      if (
        !Array.isArray(previousKey)
        || previousKey[1] !== userId
        || previousKey[2] !== organizationId
        || !isPageOnlyFilterChange(filters, previousKey[3])
      ) {
        return undefined;
      }

      return previousData;
    },
  });

  // Realtime subscription for thread updates.
  //
  // This effect used to be gated on `enabled`, so the channel was torn down on the Reply Queue
  // tab. That guard existed for a reason that has since stopped being true: it was opening a
  // channel "for a query result the tab never reads". The callback now invalidates THREE caches —
  // the thread list, the tab counts, and the campaign domain — and the counts are read on EVERY
  // tab, including Reply Queue, whose badges are always mounted. Keeping the old gate therefore
  // stranded exactly the freshness it was written to protect: a thread event would refresh the
  // list on other tabs while Reply Queue's counts silently aged, with no polling and no refetch
  // (staleTime marks data stale, it does not refetch a mounted query).
  //
  // So the channel's lifetime is now tied to IDENTITY rather than to the list query's enablement.
  // The original concern does not transfer: invalidating a disabled query is a no-op — React Query
  // will not refetch `conversation-threads` while the Reply Queue tab has it disabled — so nothing
  // is fetched for a result nobody reads. Identity still tears the channel down and rebuilds it,
  // which is what actually matters for tenant correctness.
  useEffect(() => {
    if (!user?.id || !organizationId) return;

    let pollingInterval: ReturnType<typeof setInterval> | null = null;
    const activeUserId = user.id;
    const activeOrganizationId = organizationId;

    const invalidateActiveThreadData = (): void => {
      void queryClient.invalidateQueries({
        queryKey: ['conversation-threads', activeUserId, activeOrganizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['thread-counts', activeUserId, activeOrganizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['conversation-campaigns', activeUserId, activeOrganizationId],
      });
    };

    const realtimeFilter = isAdmin
      ? `organization_id=eq.${activeOrganizationId}`
      : `user_id=eq.${activeUserId}`;

    const channel = supabase
      .channel(`threads-${activeUserId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'os_conversation_threads',
          filter: realtimeFilter,
        },
        invalidateActiveThreadData
      )
      .subscribe((status) => {
        if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !pollingInterval) {
          pollingInterval = setInterval(invalidateActiveThreadData, 30000);
        }
      });

    return () => {
      supabase.removeChannel(channel);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [queryClient, user?.id, isAdmin, organizationId]);

  return query;
};

// ─────────────────────────────────────────────────────────────────────────────
// Single-Thread Lookup — deep-link fallback, independent of the status filter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches one thread by id, unfiltered by status. `useConversationThreads`
 * only ever returns threads matching the active status filter (default
 * `needs_reply`), so a deep link to a thread that's since gone `resolved`
 * (e.g. from the lead dossier's "Review Draft" action) would otherwise be
 * silently unselectable — not absent from the app, just invisible to a
 * query that was never asking about it. `os_conversation_inbox` excludes
 * only soft-deleted threads, so any live status resolves here.
 */
export const useThreadById = (
  threadId: string | null | undefined
): ReturnType<typeof useQuery<ConversationThread | null>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ConversationThread | null>({
    queryKey: ['conversation-thread', userId, organizationId, threadId],
    enabled: !!threadId && userId !== null && organizationId !== null,
    queryFn: async () => {
      if (!threadId || userId === null || organizationId === null) return null;

      const { data, error } = await supabase
        .from('os_conversation_inbox')
        .select(THREAD_COLUMNS)
        .eq('organization_id', organizationId)
        .eq('thread_id', threadId)
        .maybeSingle();
      if (error) throw error;
      return (data as ConversationThread | null) ?? null;
    },
    staleTime: 10000,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Thread Counts Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `reply_queue` is deliberately absent from this Record: it isn't derived
 * from `os_conversation_threads.status` at all — that's the whole point of
 * the tab (see useReplyQueue.ts) — so it has no honest place in a count map
 * keyed by stored-thread status. Its count comes solely from the
 * always-mounted `useReplyQueueCount`. Widening this back to the full
 * `ThreadStatus` would make `reply_queue` look like a status this view can
 * answer for, which is exactly the false-shared-count bug this type avoids.
 */
type StoredThreadStatus = Exclude<ThreadStatus, 'reply_queue' | 'follow_up'>;

export interface ThreadCountRow {
  id: string;
  status: string;
  our_message_count: number;
  pending_draft_count: number | null;
  deleted_at: string | null;
  follow_up_due_at: string | null;
}

/**
 * `follow_up` joins `pending` as a tally that is NOT a stored status — it is a date compared to a
 * clock. It is counted here rather than by a second query so the badge and the tab read the same
 * snapshot through the same rule.
 */
export type ThreadCounts = Record<StoredThreadStatus | 'pending' | 'follow_up', number>;

const emptyThreadCounts = (): ThreadCounts => ({
  needs_reply: 0,
  waiting: 0,
  resolved: 0,
  ghosted: 0,
  active: 0,
  sent: 0,
  deleted: 0,
  all: 0,
  pending: 0,
  follow_up: 0,
});

/**
 * Derives every thread-backed Inbox tally from one snapshot. In particular,
 * `pending` sums the same `pending_draft_count` field rendered by each row;
 * PostgreSQL maintains that field with `is_actionable_pending_draft`.
 */
export const deriveThreadCounts = (
  rows: readonly ThreadCountRow[],
  now: Date = new Date()
): ThreadCounts => {
  const counts = emptyThreadCounts();
  const nowMs = now.getTime();

  rows.forEach((thread) => {
    if (thread.deleted_at) {
      counts.deleted += 1;
      return;
    }

    if (
      thread.status === 'needs_reply'
      || thread.status === 'waiting'
      || thread.status === 'resolved'
      || thread.status === 'ghosted'
      || thread.status === 'active'
    ) {
      counts[thread.status] += 1;
    }
    if (thread.our_message_count > 0) {
      counts.sent += 1;
    }
    // DUE, not scheduled — the same predicate the Follow-up query applies. A badge that counted
    // every future date would promise work the tab does not show. Deleted threads never reach here.
    if (thread.follow_up_due_at !== null) {
      const dueMs = Date.parse(thread.follow_up_due_at);
      if (!Number.isNaN(dueMs) && dueMs <= nowMs) counts.follow_up += 1;
    }
    counts.pending += thread.pending_draft_count ?? 0;
    counts.all += 1;
  });

  return counts;
};

export const useThreadCounts = (
  senderId: string,
  campaignId: string | 'all',
): ReturnType<typeof useQuery<ThreadCounts>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ThreadCounts>({
    queryKey: ['thread-counts', userId, organizationId, senderId, campaignId],
    queryFn: async () => {
      if (userId === null || organizationId === null) return emptyThreadCounts();

      const rows = await readAllRowsKeyset<ThreadCountRow, string>(
        () => {
          let queryBuilder = supabase
            .from('os_conversation_threads')
            .select('id, status, our_message_count, pending_draft_count, deleted_at, follow_up_due_at')
            .eq('organization_id', organizationId);

          if (senderId === 'unassigned') {
            queryBuilder = queryBuilder.is('sender_id', null);
          } else if (senderId !== 'all') {
            queryBuilder = queryBuilder.eq('sender_id', senderId);
          }

          if (campaignId !== 'all') {
            queryBuilder = queryBuilder.eq('heyreach_campaign_id', campaignId);
          }

          return queryBuilder;
        },
        'id',
        (thread) => thread.id,
        (error) => {
          throw error;
        },
      );

      return deriveThreadCounts(rows);
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
    // The Follow-up badge is a clock reading, and this query is mounted on every tab. Without a
    // recurring refetch the badge would sit at its mount-time value until some other event happened
    // to invalidate it — a person could become due and the number would never move.
    refetchInterval: FOLLOW_UP_REFETCH_MS,
  });
};

export const useConversationCampaigns = (): ReturnType<typeof useQuery<ConversationCampaign[]>> => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<ConversationCampaign[]>({
    queryKey: ['conversation-campaigns', userId, organizationId],
    queryFn: async () => {
      if (userId === null || organizationId === null) return [];

      // DIFF round 2, F3: this read is the CAMPAIGN DOMAIN — the set the dropdown promises is
      // complete. An unbounded select is capped by PostgREST at 1,000 rows with a 200 and no
      // warning, which is the exact bug just fixed for the tab counts; shipping it here would
      // reintroduce a silently-incomplete "complete domain" the moment an organization passes
      // that many campaigns. One row per campaign makes it unlikely, not impossible, and
      // "unlikely" is not what the word complete means.
      const data = await readAllRowsKeyset<ConversationCampaign, string>(
        () =>
          supabase
            .from('os_conversation_campaigns')
            .select('organization_id, heyreach_campaign_id, campaign_name')
            .eq('organization_id', organizationId),
        'heyreach_campaign_id',
        (campaign) => {
          if (campaign.heyreach_campaign_id === null) {
            throw new Error('Conversation campaign is missing its keyset cursor');
          }
          return campaign.heyreach_campaign_id;
        },
        (error) => {
          throw error;
        },
      );

      // Display order is restored here, where no page boundary depends on it.
      return data.sort((left, right) => {
        if (left.campaign_name === right.campaign_name) {
          return (left.heyreach_campaign_id ?? '').localeCompare(
            right.heyreach_campaign_id ?? '',
          );
        }
        if (left.campaign_name === null) return 1;
        if (right.campaign_name === null) return -1;

        const byName = left.campaign_name.localeCompare(right.campaign_name);
        if (byName !== 0) return byName;

        return (left.heyreach_campaign_id ?? '').localeCompare(
          right.heyreach_campaign_id ?? '',
        );
      });
    },
    enabled: userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};
